import { useState, useEffect } from 'react'

type FontWeight = {
  value: number
  style: 'normal' | 'italic'
}

export type Font = {
  fontFamily: string
  weights: FontWeight[]
}

// `document.fonts` is document-global and only grows over a page's lifetime, but
// `Array.from(document.fonts)` is timing-sensitive: on a component remount (no
// page reload) a fresh enumeration can come back partial — Chrome sometimes
// yields only the JS-added faces (our custom fonts) and omits the CSS-connected
// Google Fonts from the <link> stylesheet. So we accumulate into a module-level
// cache that only ever unions in new faces; a later partial read can never drop
// fonts we've already seen. Shared across all hook instances.
let fontCache: Font[] = []
let fontCacheSignature = ''
const subscribers = new Set<() => void>()

const weightKey = (value: number, style: string) => `${value}-${style}`

/** Union the current `document.fonts` into `fontCache`. Returns true if the set
 *  grew (new family or new weight/style), so callers can notify subscribers. */
const accumulate = (): boolean => {
  const byFamily = new Map<string, Set<string>>()
  for (const font of fontCache) {
    byFamily.set(font.fontFamily, new Set(font.weights.map((w) => weightKey(w.value, w.style))))
  }
  for (const face of Array.from(document.fonts)) {
    const family = face.family.replace(/["']/g, '')
    if (!byFamily.has(family)) byFamily.set(family, new Set())
    byFamily.get(family)!.add(weightKey(parseInt(face.weight) || 400, face.style))
  }

  const next: Font[] = Array.from(byFamily, ([fontFamily, keys]) => ({
    fontFamily,
    weights: Array.from(keys)
      .map((k) => {
        const [value, style] = k.split('-')
        return { value: parseInt(value || '400'), style: style as 'normal' | 'italic' }
      })
      .sort((a, b) => a.value - b.value),
  })).sort((a, b) => a.fontFamily.localeCompare(b.fontFamily))

  const signature = next.map((f) => `${f.fontFamily}:${f.weights.length}`).join('|')
  if (signature === fontCacheSignature) return false
  fontCache = next
  fontCacheSignature = signature
  return true
}

const notify = () => subscribers.forEach((fn) => fn())

/** Ensure enumerated faces are actually loaded (so the canvas has real metrics),
 *  then fold them into the cache. */
const refresh = async (triggerLoad: boolean) => {
  if (!document.fonts) {
    console.error('document.fonts is not supported in this browser')
    return
  }
  await document.fonts.ready
  if (triggerLoad) {
    await Promise.all(
      Array.from(document.fonts).map((face) =>
        document.fonts.load(`${face.style} ${face.weight} 16px "${face.family}"`).catch(() => {}),
      ),
    )
  }
  if (accumulate()) notify()
}

export const useFontsLoader = () => {
  const [fonts, setFonts] = useState<Font[]>(fontCache)
  const [fontsLoaded, setFontsLoaded] = useState(fontCache.length > 0)

  useEffect(() => {
    let cancelled = false
    const sync = () => {
      if (cancelled) return
      setFonts(fontCache)
      setFontsLoaded(true)
    }
    subscribers.add(sync)

    // Fold in whatever is loaded now (cache may already be populated from a
    // prior mount — then this is a cheap no-op), and keep folding as the Google
    // Fonts stylesheet and custom faces finish loading. `sync` runs after the
    // initial refresh so `fontsLoaded` flips even when nothing changed.
    void refresh(true).then(sync)
    const onLoadingDone = () => void refresh(false)
    document.fonts?.addEventListener('loadingdone', onLoadingDone)

    return () => {
      cancelled = true
      subscribers.delete(sync)
      document.fonts?.removeEventListener('loadingdone', onLoadingDone)
    }
  }, [])

  return { fonts, fontsLoaded }
}
